<template>
  <div class="reservation-actions">
    <md-button
      class="md-dense md-primary"
      :disabled="busy || !departureId"
      @click="save"
    >
      <md-icon>save</md-icon> Save
    </md-button>
    <md-button
      v-if="$allowed('tour.manage')"
      class="md-dense md-primary"
      :disabled="busy || !departureId || !rows.length"
      @click="saveDefault"
    >
      Save as tour default
    </md-button>
    <md-button
      class="md-dense md-primary"
      :disabled="busy || !departureId || !hasDefault"
      @click="useDefault"
      >Use tour default</md-button
    >
    <p v-if="notice" role="status">{{ notice }}</p>
  </div>
</template>

<script>
import departureApi from "@/services/api/departure";
import Swal from "sweetalert2";

export default {
  props: {
    departureId: { type: Number, default: null },
    section: { type: String, required: true },
    rows: { type: Array, required: true },
    prepare: { type: Function, required: true }
  },
  data() {
    return { busy: false, hasDefault: false, notice: "" };
  },
  watch: {
    departureId: {
      immediate: true,
      async handler(id) {
        this.hasDefault = false;
        if (!id) return;
        try {
          const response = await departureApi.getManifestDefault(
            id,
            this.section
          );
          this.hasDefault = response.data.exists;
        } catch (error) {
          this.showError(error);
        }
      }
    }
  },
  methods: {
    showError(error) {
      const data = error.response?.data;
      const details = data?.errors || data?.message;
      const text =
        details && typeof details === "object"
          ? Object.values(details)
              .flat()
              .join("\n")
          : details || "Unable to save or load reservations. Please try again.";
      return Swal.fire({ icon: "error", title: "Reservations", text });
    },
    async confirm(title, text) {
      const result = await Swal.fire({
        icon: "warning",
        title,
        text,
        showCancelButton: true,
        confirmButtonText: "Replace",
        cancelButtonText: "Cancel"
      });
      return !!result.value;
    },
    async save() {
      this.busy = true;
      try {
        this.prepare();
        const response = await departureApi.saveManifestSection(
          this.departureId,
          this.section,
          this.rows
        );
        this.$emit("update:rows", response.data.rows);
        this.notice = "Reservations saved.";
      } catch (error) {
        await this.showError(error);
      } finally {
        this.busy = false;
      }
    },
    async saveDefault() {
      this.busy = true;
      try {
        const current = await departureApi.getManifestDefault(
          this.departureId,
          this.section
        );
        this.hasDefault = current.data.exists;
        if (
          this.hasDefault &&
          !(await this.confirm(
            "Replace this tour default?",
            "Other departures will use this configuration next time they load the default. Existing reservations will stay as they are."
          ))
        )
          return;
        this.prepare();
        await departureApi.saveManifestDefault(
          this.departureId,
          this.section,
          this.rows,
          this.hasDefault
        );
        this.hasDefault = true;
        this.notice =
          "Tour default saved. This departure's reservations have not been saved.";
      } catch (error) {
        await this.showError(error);
      } finally {
        this.busy = false;
      }
    },
    async useDefault() {
      this.busy = true;
      try {
        const response = await departureApi.getManifestDefault(
          this.departureId,
          this.section
        );
        const preview = response.data;
        this.hasDefault = preview.exists;
        if (!preview.exists) {
          this.notice = "This tour has no saved default.";
          return;
        }
        if (
          this.rows.length &&
          !(await this.confirm(
            "Replace the current rows?",
            "This replaces the rows and unsaved edits in this section. Review the result and click Save to keep it."
          ))
        )
          return;
        this.$emit("update:rows", preview.rows);
        this.notice =
          "Tour default loaded. Choose a date for each reservation, then click Save.";
        if (preview.warnings.length) {
          await Swal.fire({
            icon: "warning",
            title: "Check these reservations",
            text: preview.warnings.join("\n")
          });
        }
      } catch (error) {
        await this.showError(error);
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style scoped>
.reservation-actions {
  margin-top: 1rem;
}
</style>
